import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  archiveDepartment,
  createDepartment,
  getDepartmentDetail,
  listDepartmentBranchOptions,
  listDepartmentCompanyOptions,
  listDepartmentUserOptions,
  updateDepartment,
} from '../api/departmentApi'
import { submitMasterChange } from '../../../approval/masters/api/masterApprovalApi'
import { appToast } from '../../../../components/AppToast'

function normalizeText(value) {
  const trimmed = String(value || '').trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeNullableNumber(value) {
  const text = String(value || '').trim()
  if (!text) return null

  const parsed = Number(text)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function normalizeOptionsToItems(data) {
  const arr = Array.isArray(data) ? data : []
  return arr.map((option) => ({ value: String(option.id), label: option.name }))
}

export function useDepartmentEntryScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editingDepartmentId = searchParams.get('id')

  const savingRef = useRef(false)
  const companyIdRef = useRef('')

  const [form, setForm] = useState({
    name: '',
    company_id: '',
    branch_id: '',
    department_head_id: '',
  })
  const [operationalStatus, setOperationalStatus] = useState('active')
  const [initialLabels, setInitialLabels] = useState({
    company_name: '',
    branch_name: '',
    department_head_name: '',
  })

  const [isLoading, setIsLoading] = useState(Boolean(editingDepartmentId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pendingApproval, setPendingApproval] = useState(null)

  // Keep a ref to the current company_id so branch fetchOptions closure always reads the latest value
  useEffect(() => {
    companyIdRef.current = form.company_id
  }, [form.company_id])

  // ── Dynamic fetchOptions callbacks for AppDropdown ──────────────────────────
  const fetchCompanyOptions = useCallback(async (search, page, pageSize) => {
    const data = await listDepartmentCompanyOptions({ search, page, page_size: pageSize })
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  const fetchBranchOptions = useCallback(async (search, page, pageSize) => {
    const companyId = companyIdRef.current
    if (!companyId) return { items: [], hasMore: false }
    const data = await listDepartmentBranchOptions({ search, page, page_size: pageSize, company_ids: companyId })
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  const fetchHeadOptions = useCallback(async (search, page, pageSize) => {
    const data = await listDepartmentUserOptions({ search, page, page_size: pageSize })
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  useEffect(() => {
    let active = true

    async function loadIfEditing() {
      if (!editingDepartmentId) return
      setIsLoading(true)
      setError('')

      try {
        const detail = await getDepartmentDetail(editingDepartmentId)
        if (!active) return

        setForm({
          name: detail?.name || '',
          company_id: detail?.company_id ? String(detail.company_id) : '',
          branch_id: detail?.branch_id ? String(detail.branch_id) : '',
          department_head_id: detail?.department_head_id ? String(detail.department_head_id) : '',
        })
        setInitialLabels({
          company_name: detail?.company_name || '',
          branch_name: detail?.branch_name || '',
          department_head_name: detail?.department_head_name || '',
        })
        setOperationalStatus(Number(detail?.is_archive) === 1 ? 'inactive' : 'active')
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load department details.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadIfEditing()

    return () => {
      active = false
    }
  }, [editingDepartmentId])

  const completionPercent = useMemo(() => {
    const trackedFields = ['name', 'company_id', 'branch_id', 'department_head_id']
    const filledCount = trackedFields.filter((key) => String(form[key] || '').trim().length > 0).length
    return Math.round((filledCount / trackedFields.length) * 100)
  }, [form])

  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function buildPayload() {
    return {
      name: normalizeText(form.name),
      company_id: Number(form.company_id),
      branch_id: normalizeNullableNumber(form.branch_id),
      department_head_id: normalizeNullableNumber(form.department_head_id),
      is_archive: operationalStatus === 'inactive' ? 1 : 0,
    }
  }

  async function handleSave() {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')

    if (!String(form.name || '').trim()) {
      setError('Department name is required.')
      savingRef.current = false
      return
    }

    if (!String(form.company_id || '').trim()) {
      setError('Associated company is required.')
      savingRef.current = false
      return
    }

    setIsSaving(true)

    try {
      const payload = buildPayload()
      const action = editingDepartmentId ? 'UPDATE' : 'CREATE'

      const result = await submitMasterChange({
        module_key: 'department',
        action,
        entity_id: editingDepartmentId ? Number(editingDepartmentId) : undefined,
        payload,
      })

      const msg = result?.message || (editingDepartmentId
        ? 'Department update submitted for approval.'
        : 'Department creation submitted for approval.')
      appToast.success(msg)
      setPendingApproval({
        requestId: result?.approval_request_id,
        message: msg,
      })
      setSuccess(msg)
      savingRef.current = false
      setIsSaving(false)
      navigate('/masters/department/list')
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to submit department change for approval.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  return {
    state: {
      form,
      operationalStatus,
      completionPercent,
      isLoading,
      isSaving,
      error,
      success,
      pendingApproval,
      isEditMode: Boolean(editingDepartmentId),
      initialLabels,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
      fetchCompanyOptions,
      fetchBranchOptions,
      fetchHeadOptions,
    },
  }
}
